import { CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { TransferItemRow, TransferRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 后端建单写 DRAFT、确认写 DONE（InventoryExtras / InventoryTransfer），另保留 SENT / CANCELED 兼容值。 */
type StatusFilter = 'ALL' | 'DRAFT' | 'DONE' | 'CANCELED';

/** `wl_transfer.transfer_type`（docs/db/wz-biz/11-wl-inventory.sql）。 */
const TRANSFER_TYPE_META: Record<string, string> = {
  INNER: '院内调拨',
  CROSS_ORG: '跨机构调拨',
  OUTER_PULL: '外部仓拉取',
  SUPPLIER_PUSH: '供应商直推'
};

const STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '待执行', color: 'gold' },
  NEW: { text: '待执行', color: 'gold' },
  SENT: { text: '在途', color: 'blue' },
  DONE: { text: '已完成', color: 'green' },
  CANCELED: { text: '已取消', color: 'default' }
};

/** 仓间调拨：出仓扣减 + 入仓增加（同一事务）。 */
export default function TransferList() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['transfer', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.transfer.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        transferStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['transfer', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.transfer.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const confirm = useMutation({
    mutationFn: () => hq.transfer.confirm(currentId ?? ''),
    onSuccess: async (result) => {
      message.success(`调拨完成：${result.bizNo}`);
      await queryClient.invalidateQueries({ queryKey: ['transfer'] });
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '确认调拨失败')
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<TransferRow> = [
    {
      title: '调拨单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '调出仓', dataIndex: 'outWarehouseId', width: 120 },
    { title: '调入仓', dataIndex: 'inWarehouseId', width: 120 },
    {
      title: '类型',
      dataIndex: 'transferType',
      width: 120,
      render: (value: string | null) => (value ? TRANSFER_TYPE_META[value] ?? value : '-')
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '状态',
      dataIndex: 'transferStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '业务时间', dataIndex: 'bizDate', width: 160, render: (value: string) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<TransferItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '批次', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    { title: '数量', dataIndex: 'qty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '已出', dataIndex: 'outQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '已入', dataIndex: 'inQty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const canConfirm = detail.data ? !['DONE', 'CANCELED'].includes(detail.data.transfer.transferStatus) : false;

  return (
    <Card
      className="page-card"
      title="仓间调拨"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '待执行', value: 'DRAFT' },
          { label: '已完成', value: 'DONE' },
          { label: '已取消', value: 'CANCELED' }
        ]}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="调拨单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<TransferRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无调拨单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.transfer.bizNo ?? '调拨单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={860}
        extra={
          canConfirm ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={confirm.isPending}
              onClick={() =>
                modal.confirm({
                  title: '确认调拨',
                  content: '确认后将从调出仓扣减、调入仓增加库存，并写入两条调拨流水。',
                  okText: '确认调拨',
                  onOk: () => confirm.mutateAsync()
                })
              }
            >
              确认调拨
            </Button>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.transfer.transferStatus]?.text ?? detail.data.transfer.transferStatus}
              </Descriptions.Item>
              <Descriptions.Item label="类型">{detail.data.transfer.transferType || '-'}</Descriptions.Item>
              <Descriptions.Item label="调出仓">{detail.data.transfer.outWarehouseId}</Descriptions.Item>
              <Descriptions.Item label="调入仓">{detail.data.transfer.inWarehouseId}</Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(detail.data.transfer.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="金额合计">{formatAmount(detail.data.transfer.totalAmount)}</Descriptions.Item>
            </Descriptions>
            <Table<TransferItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
